import React, { useMemo } from 'react';
import { interpolate, spring, useCurrentFrame, useVideoConfig } from 'remotion';
import type { CaptionWord } from '../../schemas';
import { HERO_ACCENT, type HeroAspect } from '../schema';
import { HERO_FONTS } from '../fonts';
import { activeCaptionPhrase, groupCaptionPhrases } from '../timeline';

/**
 * Phrase-level captions in the lower-third safe zone; the word being spoken is highlighted in HERO_ACCENT.
 * `words` are CaptionSchema words relative to content start; the component is timed off the composition frame,
 * so render it at the composition root (outside TransitionSeries).
 */
export const HeroCaptions: React.FC<{ words: CaptionWord[]; aspect: HeroAspect }> = ({ words, aspect }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const currentMs = (frame / fps) * 1000;
  const phrases = useMemo(() => groupCaptionPhrases(words), [words]);
  const phrase = activeCaptionPhrase(phrases, currentMs);
  if (!phrase) return null;

  const vertical = aspect === '9:16';
  const framesSincePhrase = frame - Math.round((phrase.startMs / 1000) * fps);
  const enter = interpolate(framesSincePhrase, [0, 5], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  const enterY = interpolate(enter, [0, 1], [14, 0]);

  return (
    <div
      style={{
        position: 'absolute',
        left: '6%',
        right: '6%',
        bottom: vertical ? '21%' : '18%',
        display: 'flex',
        justifyContent: 'center',
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          opacity: enter,
          transform: `translateY(${enterY}px)`,
          maxWidth: '92%',
          padding: vertical ? '16px 30px' : '14px 26px',
          borderRadius: 18,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'baseline',
          columnGap: '0.3em',
          fontFamily: HERO_FONTS.caption,
          fontWeight: 700,
          fontSize: vertical ? 50 : 44,
          lineHeight: 1.25,
          color: '#FFFFFF',
          textShadow: '0 2px 8px rgba(0,0,0,0.8)',
        }}
      >
        {phrase.words.map((word, i) => {
          const isCurrent = currentMs >= word.startMs && currentMs < word.endMs;
          const isPast = currentMs >= word.endMs;
          const wordFrame = frame - Math.round((word.startMs / 1000) * fps);
          const pop = isCurrent || isPast ? spring({ frame: wordFrame, fps, config: { damping: 12, stiffness: 240, mass: 0.5 } }) : 0;
          const scale = isCurrent ? interpolate(pop, [0, 1], [1, 1.06]) : 1;
          return (
            <span
              key={`${word.text}-${i}`}
              style={{
                display: 'inline-block',
                color: isCurrent ? HERO_ACCENT : '#FFFFFF',
                opacity: isPast ? 0.92 : 1,
                transform: `scale(${scale})`,
              }}
            >
              {word.text}
            </span>
          );
        })}
      </div>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
